import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import api from "@/lib/api";
import { Globe2, MapPin, Users, Layers, Loader2 } from "lucide-react";

const fmtEuro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const fmtInt = (n) => new Intl.NumberFormat("it-IT").format(n || 0);
const REGION_COLORS = ["#0A84FF","#FF9500","#AF52DE","#34C759","#FF375F","#5AC8FA","#FFD60A","#EC4899","#22D3EE","#F97316"];
const colorFor = (i) => REGION_COLORS[i % REGION_COLORS.length];

const BAND_META = [
  { key: "red",      label: "Rosso · ≤-30%",      color: "#FF3B30" },
  { key: "yellow",   label: "Giallo · -20%",      color: "#B27F00" },
  { key: "green",    label: "Verde · -10%",       color: "#1F7A3A" },
  { key: "cyan",     label: "Ciano · -0.1/-9.9%", color: "#00A6B7" },
  { key: "positive", label: "Sopra media",         color: "#0A84FF" },
  { key: "unknown",  label: "n/d",                 color: "#8E8E93" },
];

const BarTip = () => null; // (bar chart removed — replaced by RegionTrend below)

// Multi-line €/m² trend per region with a neon glow underlay and a dashed
// linear-regression projection over the next 3 months. Reads its data from
// the /analytics/global-overview `region_timeline` field.
const RegionTrend = ({ timeline }) => {
  if (!timeline || !timeline.length) {
    return <div className="h-full flex items-center justify-center text-slate-400 text-xs">Dati insufficienti.</div>;
  }
  const regions = timeline.slice(0, 8);
  const monthSet = new Set();
  regions.forEach((r) => r.points.forEach((p) => monthSet.add(p.month)));
  const monthsAsc = [...monthSet].sort();
  if (!monthsAsc.length) return null;
  const nextMonths = [];
  const [ly, lm] = monthsAsc[monthsAsc.length - 1].split("-").map(Number);
  let y = ly, m = lm;
  for (let i = 0; i < 3; i++) { m += 1; if (m > 12) { m = 1; y += 1; } nextMonths.push(`${y}-${String(m).padStart(2, "0")}`); }
  const allMonths = [...monthsAsc, ...nextMonths];
  const chartData = allMonths.map((month) => ({ month }));

  regions.forEach((r) => {
    const map = new Map(r.points.map((p) => [p.month, p.eur_mq]));
    chartData.forEach((row) => { if (map.has(row.month)) row[r.region] = map.get(row.month); });
    const xs = [], ys = [];
    r.points.forEach((p) => { const idx = monthsAsc.indexOf(p.month); if (idx >= 0) { xs.push(idx); ys.push(p.eur_mq); } });
    if (xs.length < 2) return;
    const n = xs.length;
    const sumX = xs.reduce((a, b) => a + b, 0);
    const sumY = ys.reduce((a, b) => a + b, 0);
    const sumXY = xs.reduce((a, x, i) => a + x * ys[i], 0);
    const sumXX = xs.reduce((a, x) => a + x * x, 0);
    const denom = n * sumXX - sumX * sumX;
    if (!denom) return;
    const slope = (n * sumXY - sumX * sumY) / denom;
    const intercept = (sumY - slope * sumX) / n;
    const lastIdx = monthsAsc.length - 1;
    const projKey = `${r.region}__proj`;
    chartData[lastIdx][projKey] = map.get(monthsAsc[lastIdx]);
    nextMonths.forEach((_mm, i) => {
      chartData[monthsAsc.length + i][projKey] = Math.max(0, Math.round(intercept + slope * (lastIdx + 1 + i)));
    });
  });

  return (
    <ResponsiveContainer>
      <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
        <defs>
          {regions.map((r, i) => (
            <filter key={i} id={`comm-glow-${i}`} x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="3.5" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          ))}
        </defs>
        <CartesianGrid stroke="#eef2f7" vertical={false} />
        <XAxis dataKey="month" tick={{ fontSize: 9, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false}
          tickFormatter={(mm) => { const [yy, m2] = String(mm).split("-"); return `${m2}/${String(yy).slice(2)}`; }} />
        <YAxis tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} width={44}
          tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
          label={{ value: "€/m²", angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#94a3b8" } }} />
        <Tooltip
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            const [yy, mm] = String(label).split("-");
            const seen = new Set(); const rows = [];
            payload.forEach((p) => {
              const base = p.dataKey.replace(/__proj$/, "");
              if (seen.has(base)) return;
              seen.add(base);
              rows.push({ region: base, value: p.value, projected: p.dataKey.endsWith("__proj"), color: p.color });
            });
            return (
              <div className="bg-slate-900 text-white text-[11px] rounded-lg px-3 py-2 shadow-xl min-w-[160px]">
                <div className="font-semibold uppercase tracking-wide mb-1">{mm}/{yy}</div>
                {rows.map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: r.color }} />
                    <span className="text-slate-200 flex-1 truncate">{r.region}</span>
                    <span className="font-mono-num font-bold" style={{ color: r.color }}>{fmtEuro(r.value)}</span>
                    {r.projected && <span className="text-[9px] uppercase tracking-widest text-slate-400">proj.</span>}
                  </div>
                ))}
              </div>
            );
          }}
        />
        <Legend verticalAlign="bottom" iconType="plainline" wrapperStyle={{ fontSize: 10, paddingTop: 8 }}
          formatter={(v) => (v || "").endsWith("__proj") ? null : <span className="text-slate-700">{v}</span>} />
        {regions.map((r, i) => {
          const c = colorFor(i);
          return (
            <React.Fragment key={r.region}>
              <Line type="monotone" dataKey={r.region} name={r.region} stroke={c} strokeWidth={7} strokeOpacity={0.45}
                dot={false} activeDot={false} filter={`url(#comm-glow-${i})`}
                animationDuration={900} animationBegin={i * 60} legendType="none" connectNulls />
              <Line type="monotone" dataKey={r.region} name={r.region} stroke={c} strokeWidth={2.25}
                dot={{ r: 2.5, fill: c, stroke: "#fff", strokeWidth: 1 }}
                activeDot={{ r: 5, fill: c, stroke: "#fff", strokeWidth: 2 }}
                animationDuration={900} animationBegin={i * 60} connectNulls />
              <Line type="monotone" dataKey={`${r.region}__proj`} name={`${r.region}__proj`} stroke={c} strokeWidth={1.75}
                strokeDasharray="5 4"
                dot={{ r: 2, fill: "#fff", stroke: c, strokeWidth: 1.5 }}
                activeDot={false} isAnimationActive={false} legendType="none" connectNulls />
            </React.Fragment>
          );
        })}
      </LineChart>
    </ResponsiveContainer>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, testId, tint = "#0A84FF" }) => (
  <div className="ios-card p-4 bg-white" data-testid={testId}>
    <div className="flex items-start gap-3">
      <div
        className="w-9 h-9 rounded-xl text-white flex items-center justify-center shrink-0"
        style={{ background: `linear-gradient(135deg, ${tint}, ${tint}CC)` }}
      >
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </div>
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500 font-semibold">{label}</div>
        <div className="font-display font-bold text-2xl tracking-tight text-slate-900 leading-none mt-1 font-mono-num">{value}</div>
        {sub && <div className="text-[10px] text-slate-500 mt-1">{sub}</div>}
      </div>
    </div>
  </div>
);

// Light-theme community stats — same shape as the old GlobalAnalyticsRow
// but without the dark navy background (per user request).
const CommunityStats = ({ className = "", titleHidden = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/analytics/global-overview");
        if (!cancelled) setData(r.data);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const totals = data?.totals || { total_count: 0, boards_count: 0, contributors_count: 0, regions_count: 0, avg_prezzo: 0, avg_eur_mq: 0 };
  const byRegion = (data?.by_region || []).slice(0, 8);
  const dist = data?.scost_distribution || {};
  const distTotal = Object.values(dist).reduce((a, b) => a + b, 0);

  return (
    <section className={`relative ${className}`} data-testid="community-stats-section">
      {!titleHidden && (
        <div className="flex items-end justify-between mb-4">
          <div>
            <div
              className="text-[10px] uppercase tracking-[0.3em] font-semibold flex items-center gap-2"
              style={{ color: "#000000", textShadow: "0 0 3px rgba(255,255,255,1), 0 0 3px rgba(255,255,255,0.9)" }}
            >
              <Globe2 className="w-3.5 h-3.5" /> Statistiche community · anonime
            </div>
            <h2
              className="font-display font-bold text-xl leading-tight mt-1"
              style={{ color: "#000000", textShadow: "0 0 3px rgba(255,255,255,1), 0 0 3px rgba(255,255,255,0.9)" }}
            >
              Il mercato secondo ImmoMap
            </h2>
            <p
              className="text-[11px] mt-1 max-w-xl"
              style={{ color: "#000000", textShadow: "0 0 3px rgba(255,255,255,1), 0 0 3px rgba(255,255,255,0.9)" }}
            >
              Aggregati da tutte le schede della piattaforma. Nessun indirizzo, nessun utente identificato — solo pattern di mercato.
            </p>
          </div>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
          ) : (
            <div
              className="text-[11px] font-mono-num text-right"
              style={{ color: "#000000", textShadow: "0 0 3px rgba(255,255,255,1), 0 0 3px rgba(255,255,255,0.9)" }}
            >
              {fmtInt(totals.total_count)} immobili · {fmtInt(totals.contributors_count)} contributor
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KpiCard icon={Layers} label="Immobili totali" value={fmtInt(totals.total_count)} sub={`${fmtInt(totals.boards_count)} schede attive`} testId="kpi-total" tint="#0A84FF" />
        <KpiCard icon={MapPin} label="Regioni coperte" value={fmtInt(totals.regions_count)} sub={`Top: ${byRegion[0]?.region || "—"}`} testId="kpi-regions" tint="#FF9500" />
        <KpiCard icon={Globe2} label="€/m² medio globale" value={fmtEuro(totals.avg_eur_mq)} sub={`Prezzo medio ${fmtEuro(totals.avg_prezzo)}`} testId="kpi-eurmq" tint="#34C759" />
        <KpiCard icon={Users} label="Contributor" value={fmtInt(totals.contributors_count)} sub="Utenti attivi (anonimi)" testId="kpi-contrib" tint="#AF52DE" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="ios-card p-5 lg:col-span-2 bg-white" data-testid="global-region-chart">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
                <MapPin className="w-3.5 h-3.5 text-[#12466F]" strokeWidth={1.75} />
                Trend €/m² per regione
              </div>
              <div className="text-sm text-slate-600 mt-1">Media mensile per regione + proiezione a 3 mesi</div>
            </div>
          </div>
          <div className="h-64 min-h-[260px]" style={{ width: "100%" }}>
            <RegionTrend timeline={data?.region_timeline} />
          </div>
        </div>

        <div className="ios-card p-5 bg-white" data-testid="global-scost-distribution">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
                <Globe2 className="w-3.5 h-3.5 text-[#12466F]" strokeWidth={1.75} />
                Distribuzione scostamenti
              </div>
              <div className="text-sm text-slate-600 mt-1">Quanti affari e sopra-media</div>
            </div>
          </div>
          <ul className="space-y-2" data-testid="global-dist-list">
            {BAND_META.map(({ key, label, color }) => {
              const v = dist[key] || 0;
              const pct = distTotal > 0 ? (v / distTotal) * 100 : 0;
              return (
                <li key={key} className="flex items-center gap-2" data-testid={`dist-band-${key}`}>
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                  <span className="text-[11px] text-slate-600 w-40 shrink-0">{label}</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct.toFixed(1)}%`, backgroundColor: color }} />
                  </div>
                  <span className="text-[11px] font-mono-num text-slate-700 w-10 text-right">{fmtInt(v)}</span>
                </li>
              );
            })}
          </ul>
          <div className="mt-3 text-[10px] text-slate-400">
            Su {fmtInt(distTotal)} immobili con €/m² calcolabile.
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommunityStats;
